/** Shared sprite catalogue and cached Canvas rasterization for thumbnails and GPU textures. */

import { CHARACTER_SPRITES } from './art/characters'
import { TILE_SPRITES } from './art/tiles'
import { drawSprite, type Sprite } from './sprite'

function merge(...records: Record<string, Sprite>[]): Map<string, Sprite> {
  const out = new Map<string, Sprite>()
  for (const record of records) {
    for (const [key, sprite] of Object.entries(record)) out.set(key, sprite)
  }
  return out
}

/** Every sprite in the game, keyed by its art-module key. */
export const SPRITES: Map<string, Sprite> = merge(
  TILE_SPRITES,
  CHARACTER_SPRITES,
)

/**
 * Reverse lookup, so `renderAtlas` can find the cache entry for a sprite that
 * was fetched by reference. Sprites not in the atlas get a synthetic key.
 */
const KEYS_BY_SPRITE = new WeakMap<Sprite, string>()
for (const [key, sprite] of SPRITES) KEYS_BY_SPRITE.set(sprite, key)

let anonymousUid = 0

function keyFor(sprite: Sprite): string {
  const known = KEYS_BY_SPRITE.get(sprite)
  if (known !== undefined) return known
  anonymousUid += 1
  const generated = `anon:${anonymousUid}`
  KEYS_BY_SPRITE.set(sprite, generated)
  return generated
}

/** Create an offscreen canvas, always at least 1x1. */
function makeCanvas(w: number, h: number): HTMLCanvasElement {
  const canvas = document.createElement('canvas')
  canvas.width = Math.max(1, Math.floor(w))
  canvas.height = Math.max(1, Math.floor(h))
  return canvas
}

/** Sprite lookup. Returns undefined for unknown keys. */
export function getSprite(key: string): Sprite | undefined {
  return SPRITES.get(key)
}

/** Rendered pixel size of a key, or 1x1 when the key is unknown. */
export function spriteSize(key: string): { w: number; h: number } {
  const sprite = SPRITES.get(key)
  if (sprite === undefined) return { w: 1, h: 1 }
  return { w: sprite.w, h: sprite.h }
}

const RENDERED = new Map<string, HTMLCanvasElement>()

/**
 * The pre-rendered bitmap for a sprite, 1:1 and cached. The returned canvas is
 * shared — treat it as read-only and blit it, do not draw into it.
 */
export function renderAtlas(sprite: Sprite): HTMLCanvasElement {
  const key = keyFor(sprite)
  const hit = RENDERED.get(key)
  if (hit !== undefined) return hit

  const canvas = makeCanvas(sprite.w, sprite.h)
  const ctx = canvas.getContext('2d')
  if (ctx !== null) {
    ctx.imageSmoothingEnabled = false
    drawSprite(ctx, sprite, 0, 0)
  }
  RENDERED.set(key, canvas)
  return canvas
}
